import "@/App.css";
import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { CartProvider } from "./contexts/CartContext";
import { PlayerProvider } from "./contexts/PlayerContext";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import BeatCatalog from "./components/BeatCatalog";
import FeaturedBeats from "./components/FeaturedBeats";
import ForgeSection from "./components/ForgeSection";
import AudioPlayer from "./components/AudioPlayer";
import CartDrawer from "./components/CartDrawer";
import SuccessPage from "./pages/SuccessPage";
import ForgeSuccessPage from "./pages/ForgeSuccessPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";

const AdminLogin = React.lazy(() => import("./admin/AdminLogin"));
const AdminLayout = React.lazy(() => import("./admin/AdminLayout"));
const AdminDashboard = React.lazy(() => import("./admin/AdminDashboard"));
const AdminBeats = React.lazy(() => import("./admin/AdminBeats"));
const AdminOrders = React.lazy(() => import("./admin/AdminOrders"));
const AdminForge = React.lazy(() => import("./admin/AdminForge"));
const ProtectedRoute = React.lazy(() => import("./admin/ProtectedRoute"));

function SiteFooter() {
  // Privacy & Cookie policies are our own in-brand pages (/privacy,
  // /cookie-policy). Iubenda is kept only for the consent banner + the
  // "Manage Cookies" preferences (window._iub), which still gate analytics.
  return (
    <footer data-testid="footer" className="border-t border-[#1A1A1A]">
      <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
        <Link
          to="/"
          data-testid="footer-lockup"
          className="flex items-center gap-4 shrink-0"
        >
          <img src="/brand/logo.png" alt="Sanctum Sonics emblem" className="h-14 w-auto" />
          <img src="/brand/logotype.png" alt="Sanctum Sonics" className="h-5 md:h-6 w-auto" />
        </Link>
        <div className="text-center md:text-right">
          <p className="text-xs text-[#737373] mb-4">
            &copy; 2026 Sanctum Sonics / La Grotta Records. All Rights Reserved.
          </p>
          <div className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-2 text-[10px] tracking-[0.2em] uppercase text-[#737373]">
            <Link to="/privacy" className="hover:text-gold transition-colors">
              Privacy Policy
            </Link>
            <Link to="/cookie-policy" className="hover:text-gold transition-colors">
              Cookie Policy
            </Link>
            <button
              type="button"
              onClick={() => {
                if (window._iub && window._iub.cs && window._iub.cs.api) {
                  window._iub.cs.api.openPreferences();
                }
              }}
              className="uppercase tracking-[0.2em] hover:text-gold transition-colors"
            >
              Manage Cookies
            </button>
            <a
              href="mailto:privacy@sanctumsonics.com?subject=GDPR%20erasure%20request"
              className="hover:text-gold transition-colors"
            >
              Delete My Data
            </a>
            <Link
              to="/admin/login"
              data-testid="footer-admin-link"
              className="hover:text-gold transition-colors"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function Storefront() {
  return (
    <>
      <Hero />
      <FeaturedBeats />
      <ForgeSection />
      <SiteFooter />
    </>
  );
}

function CatalogPage() {
  return (
    <>
      <BeatCatalog />
      <SiteFooter />
    </>
  );
}

function StorefrontShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  if (isAdmin) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-[#050505] text-[#EAEAEA] flex items-center justify-center text-xs tracking-[0.2em] uppercase">Loading...</div>}>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="beats" element={<AdminBeats />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="forge" element={<AdminForge />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <Routes>
        <Route path="/" element={<Storefront />} />
        <Route path="/catalog" element={<CatalogPage />} />
        <Route path="/success" element={<SuccessPage />} />
        <Route path="/forge-success" element={<ForgeSuccessPage />} />
        <Route path="/privacy" element={<><PrivacyPolicy /><SiteFooter /></>} />
        <Route path="/cookie-policy" element={<><CookiePolicy /><SiteFooter /></>} />
      </Routes>
      <AudioPlayer />
      <CartDrawer />
    </div>
  );
}

function App() {
  return (
    <CartProvider>
      <PlayerProvider>
        <BrowserRouter>
          <StorefrontShell />
        </BrowserRouter>
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#0F0F0F",
              border: "1px solid #1A1A1A",
              color: "#EAEAEA",
            },
          }}
        />
      </PlayerProvider>
    </CartProvider>
  );
}

export default App;
